import React from 'react';
import { Button, Col, Dropdown, Menu, Popconfirm, Row, Space } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { PiPencilSimpleLine, PiTrashSimple } from 'react-icons/pi';
import { RiArrowLeftLine, RiErrorWarningLine } from 'react-icons/ri';
import { DownOutlined } from '@ant-design/icons';
import { MdOutlineUnpublished } from 'react-icons/md';
import { HasRights, PrivateComponent } from '../../../../../../components/HasRights/HasRights';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import urlPagePushNotifications from '../../../../../../urls/urlPagePushNotifications';
import urlPagePushNotificationsSchedule from '../../../../../../urls/urlPagePushNotificationsSchedule';
import urlPagePushNotificationScheduleEdit from '../../../../../../urls/urlPagePushNotificationScheduleEdit';
import { Permissions } from '../../../../../../const/permissions';
import handleResponse from '../../../../../../lib/handleResponse';
import canInteractWithSchedule from '../../../../../../lib/canInteractWithSchedule';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../../layout/components/action-button';
import NotificationScheduleDetailsCard from '../../../../../Content/components/NotificationScheduleDetailsCard';
import { EMPTY_VALUE_PLACEHOLDER } from '../../../../../../const/system';
import {
  useDeletePushNotificationScheduleMutation,
  useGetSpecificPushNotificationScheduleQuery,
  useUnpublishPushNotificationScheduleMutation,
} from '../../../../api/pushNotificationsScheduleApiSlice';



const PagePushNotificationScheduleDetails = () => {
  const { scheduleId } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();

  const { data: schedule = {}, isFetching } = useGetSpecificPushNotificationScheduleQuery({ scheduleId });
  const [ unpublishPushNotificationSchedule, { isLoading: isUnpublishPushNotificationScheduleLoading } ] = useUnpublishPushNotificationScheduleMutation();
  const [ deleteSchedule, { isLoading: isScheduleDeleting } ] = useDeletePushNotificationScheduleMutation();

  const isPublished = Boolean(schedule?.is_published);

  const handleDelete = (id) => {
    deleteSchedule(id)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully', [ () => navigate(urlPagePushNotificationsSchedule()) ]))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleUnpublish = () => {
    unpublishPushNotificationSchedule(scheduleId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };


  const allowRenderActions = HasRights([ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.EDIT ]) ||
    (isPublished && HasRights([ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.UNPUBLISH ])) ||
    (!isPublished && HasRights([ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.DELETE ]));

  const actionItems = (
    <Menu>
      <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.EDIT ]}>
        <Menu.Item
          key="edit"
          icon={<PiPencilSimpleLine size={18} />}
          onClick={() => navigate(urlPagePushNotificationScheduleEdit({ scheduleId: schedule.id }))}
        >
          <IntlMessages id="ui-general-edit" />
        </Menu.Item>
      </PrivateComponent>
      {isPublished ? (
        <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.UNPUBLISH ]}>
          <Menu.Item
            key="unpublish"
            icon={<MdOutlineUnpublished size={18} />}
            onClick={handleUnpublish}
          >
            <IntlMessages id="ui-general-unpublish" />
          </Menu.Item>
        </PrivateComponent>
      ) : (
        <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.DELETE ]}>
          <Popconfirm
            title={<IntlMessages id="ui-general-delete-confirm-message" />}
            placement="topLeft"
            onConfirm={() => handleDelete(schedule.id)}
            okText={<IntlMessages id="ui-general-yes" />}
            cancelText={<IntlMessages id="ui-general-no" />}
            icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
            okButtonProps={{ danger: true }}
          >
            <Menu.Item
              key="delete"
              icon={<PiTrashSimple size={18} />}
              onClick={() => {}}
            >
              <IntlMessages id="ui-general-delete" />
            </Menu.Item>
          </Popconfirm>
        </PrivateComponent>
      )}
    </Menu>
  );

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPagePushNotifications()}>
              <IntlMessages id='push-notifications-breadcrumbs' />
            </Link>
          }
          breadCrumbParent2={
            <Link to={urlPagePushNotificationsSchedule()}>
              <IntlMessages id='push-notifications-schedule-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={schedule?.id ?? EMPTY_VALUE_PLACEHOLDER}
        />

        <Col md={10} span={24} className='text-end'>
          <Space size='middle'>
            {canInteractWithSchedule(schedule) && allowRenderActions && (
              <Dropdown overlay={actionItems}>
                <Button onClick={(ev) => ev.preventDefault()}>
                  <Space size='middle'>
                    <IntlMessages id='ui-general-action' />
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            )}
            <ActionButton
              title={intl.formatMessage({ id: 'ui-general-back-to-list' })}
              icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
              onClick={() => navigate(urlPagePushNotificationsSchedule())}
            />
          </Space>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <NotificationScheduleDetailsCard
            isLoadingCard={isScheduleDeleting || isUnpublishPushNotificationScheduleLoading}
            isFetching={isFetching}
            data={schedule}
          />
        </Col>
      </Row>
    </>
  );
};

export default PagePushNotificationScheduleDetails;
